import React, { useEffect, useState } from "react";
import InfoCard from "@components/display/InfoCard/InfoCard";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { formatDate } from "@util/string";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { fetchUpcomingService } from "@services/upcomingServiceService";
import { UpcomingService } from "@models/UpcomingService";
import { Service } from "@models/Service";

const RsvpPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const [upcomingService, setUpcomingService] = useState<UpcomingService>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchUpcomingService(id);
                setUpcomingService(data);
            } catch (err) {
                setError("Failed to fetch services.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [id]);

    const renderServiceCard = (service: Service, index: number) => {
    // Total attendees
        const totalAttendees = service.rsvps?.reduce((sum, rsvp) => sum + (rsvp.attendees || 0), 0) || 0;

        // Each RSVP becomes an InfoCard item
        const rsvpItems = service.rsvps?.map(
            (rsvp) => `${rsvp.name} (${rsvp.attendees} attending)`,
        ) || [];

        return (
            <InfoCard
                key={index}
                title={`${service.type || "Service"}${service.dateTime ? ` - ${formatDate(service.dateTime)}` : ""}`}
                items={[
                    `Location: ${service.location || "TBA"}`,
                    "", // Gap
                    `Total Attendees: ${totalAttendees}`,
                    "", // Gap
                    ...rsvpItems,
                ]}
            />
        );
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!upcomingService?.services || upcomingService.services.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No Services Yet</div>;
        }

        return (
            <div className={commonStyles.itemList}>
                {upcomingService.services.map((service, idx) => renderServiceCard(service, idx))}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            {!loading && !error && (
                <div className={commonStyles.headerContainer}>
                    <h1>{`${upcomingService?.firstName} ${upcomingService?.lastName} - RSVPs`}</h1>
                    <div className={commonStyles.buttonGroup}>
                        <Button
                            variant="submit"
                            size="medium"
                            onClick={() => navigate(-1)}
                        >
                            Finish
                        </Button>
                    </div>
                </div>
            )}
            {renderContent()}
        </div>
    );
};

export default RsvpPage;
